import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { Avatar } from '../ui/Avatar';
import { Icon } from '../ui/Icon';
import { userInitials } from '../../lib/brandAvatars';
import { userDisplayName } from '../../lib/users';
import { NotificationsBell } from './NotificationsBell';
import SearchBar from './SearchBar';

interface TopbarProps {
  breadcrumb?: { label: string; to?: string }[];
  /** Opens the sidebar drawer (the button only shows on phones). */
  onMenu?: () => void;
}

export const Topbar = ({ breadcrumb = [], onMenu }: TopbarProps) => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const displayName = user
    ? userDisplayName({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        username: user.username,
      })
    : '';

  return (
    <header className="topbar">
      <button type="button" className="icon-btn topbar-menu" aria-label="Open menu" onClick={onMenu}>
        <Icon name="menu" size={18} />
      </button>
      <div className="crumb">
        {breadcrumb.map((c, i) => {
          const isLast = i === breadcrumb.length - 1;
          const inner = (
            <span
              className={isLast ? 'current' : ''}
              style={{ marginLeft: i > 0 ? 6 : 0, marginRight: 6 }}
            >
              {c.label}
            </span>
          );
          return (
            <span key={i} className={isLast ? 'crumb-last' : 'crumb-parent'}>
              {i > 0 && <span className="sep">/</span>}
              {c.to && !isLast ? (
                <Link
                  to={c.to}
                  style={{
                    color: 'inherit',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget.querySelector('span') as HTMLElement).style.color =
                      'var(--ink)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget.querySelector('span') as HTMLElement).style.color = '';
                  }}
                >
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </span>
          );
        })}
      </div>

      <div style={{ flex: 1 }} />

      <div className="topbar-actions" style={{ position: 'relative' }}>
        <SearchBar />
        <NotificationsBell />

        {user && (
          <div ref={wrapRef} style={{ position: 'relative', paddingLeft: 4 }}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Account menu"
              title={displayName}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Avatar
                initials={userInitials({
                  first_name: user.first_name,
                  last_name: user.last_name,
                  username: user.username,
                  initials: '',
                })}
                imageUrl={user.clerkImageUrl}
                size={26}
                title={displayName}
              />
            </button>
            {menuOpen && (
              <div
                className="popover"
                style={{
                  top: 'calc(100% + 6px)',
                  right: 0,
                  minWidth: 220,
                  padding: 0,
                }}
              >
                <div
                  style={{
                    padding: '10px 12px 8px',
                    borderBottom: '1px solid var(--border-soft)',
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>
                    {displayName}
                  </div>
                  {user.email && (
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                      {user.email}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="popover-item"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  style={{ fontSize: 12, color: 'var(--ink-2)' }}
                >
                  <Icon name="logOut" size={13} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Topbar;
